import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowRight,
  Phone,
  CheckCircle2,
  ChevronDown,
  MapPin,
} from "lucide-react";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import QuoteFormDialog from "../../components/QuoteFormDialog";
import { useLang } from "../../i18n/LanguageContext";
import { SERVICE_PAGES, CITIES } from "./configs";

import NotFound from "../../seo/NotFound";

const Breadcrumb = ({ label }) => (
  <nav className="flex items-center gap-2 text-xs text-zinc-500 mb-8">
    <Link to="/" className="hover:text-sky-400">
      Home
    </Link>
    <span>/</span>
    <span className="text-zinc-300">{label}</span>
  </nav>
);

const FAQItem = ({ q, a, index }) => {
  const [open, setOpen] = useState(index === 0);
  return (
    <div
      data-testid={`faq-item-${index}`}
      className="border-b border-white/10 py-6"
    >
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-start justify-between gap-6 text-left group"
      >
        <span className="font-display font-bold text-white text-lg sm:text-xl leading-tight group-hover:text-sky-400 transition-colors">
          {q}
        </span>
        <ChevronDown
          size={20}
          className={`shrink-0 text-sky-400 transition-transform duration-300 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      <div
        className={`grid transition-all duration-500 ease-out ${
          open ? "grid-rows-[1fr] mt-4 opacity-100" : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="overflow-hidden">
          <p className="text-zinc-400 leading-relaxed text-sm sm:text-base max-w-3xl">
            {a}
          </p>
        </div>
      </div>
    </div>
  );
};

const ServicePage = () => {
  const { slug } = useParams();
  const cfg = SERVICE_PAGES[slug];
  const [quoteOpen, setQuoteOpen] = useState(false);
  const { t } = useLang();


  if (!cfg) return <NotFound />;

  const openQuote = () => setQuoteOpen(true);

  return (
    <div className="bg-[#09090b] min-h-screen" data-testid={`service-page-${slug}`}>
      <Navbar onQuoteClick={openQuote} />

      {/* HERO */}
      <section className="relative pt-32 pb-20 sm:pt-40 sm:pb-28 overflow-hidden hero-gradient">
        <div className="absolute inset-0">
          <img
            src={cfg.heroImage}
            alt={cfg.h1}
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 hero-image-mask"></div>
          <div className="absolute inset-0 grain"></div>
        </div>

        <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
          <Breadcrumb label={cfg.eyebrow} />

          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-8 space-y-7">
              <div className="flex items-center gap-3">
                <span className="h-px w-12 bg-sky-400"></span>
                <span className="eyebrow">{cfg.eyebrow}</span>
              </div>
              <h1
                data-testid="service-h1"
                className="font-display font-black text-white text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[0.95] tracking-tighter"
              >
                {cfg.h1}
              </h1>
              <p className="text-zinc-300 text-base sm:text-lg leading-relaxed max-w-3xl">
                {cfg.intro}
              </p>
              <p className="text-sky-200 text-sm leading-relaxed max-w-3xl" data-testid="service-quote-availability">
                {t.services.quote_availability}
              </p>
              <div className="flex flex-wrap gap-4 pt-2">
                <button
                  data-testid="service-cta-quote"
                  onClick={openQuote}
                  className="btn-primary"
                >
                  {cfg.cta}
                  <ArrowRight size={16} />
                </button>
                <a
                  href="tel:+15105079318"
                  data-testid="service-cta-call"
                  className="btn-secondary"
                >
                  <Phone size={16} />
                  Call (510) 507-9318
                </a>
              </div>

              {/* Trust strip */}
              <div className="flex flex-wrap items-center gap-x-8 gap-y-3 pt-6">
                {t.hero.badges.map((label) => <Trust key={label} Icon={CheckCircle2} label={label} />)}
              </div>
            </div>

            {/* Side card */}
            <aside className="lg:col-span-4 lg:sticky lg:top-32 self-start">
              <div className="border border-sky-400/40 bg-zinc-950/80 backdrop-blur-xl p-8 space-y-5">
                <div className="eyebrow">Free Quote</div>
                <div className="font-display font-bold text-white text-2xl leading-tight">
                  Talk to a real installer today
                </div>
                <a
                  href="tel:+15105079318"
                  className="block text-3xl font-display font-black text-sky-400 hover:text-sky-300 transition-colors"
                >
                  (510) 507-9318
                </a>
                <p className="text-zinc-400 text-sm">
                  {t.cta.subtitle}
                </p>
                <button
                  onClick={openQuote}
                  data-testid="service-side-quote"
                  className="btn-primary w-full justify-center"
                >
                  Request Quote
                </button>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-24 bg-[#09090b]">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-12 bg-sky-400"></span>
            <span className="eyebrow">Why Xavier</span>
          </div>
          <h2 className="font-display font-black text-white text-3xl sm:text-5xl leading-tight tracking-tighter mb-14 max-w-3xl">
            Built to factory spec. Finished like a custom build.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/5">
            {cfg.benefits.map((b, i) => (
              <div
                key={i}
                data-testid={`benefit-${i}`}
                className="bg-zinc-950 p-8 sm:p-10"
              >
                <div className="text-sky-400 mb-5">
                  <CheckCircle2 size={26} strokeWidth={1.5} />
                </div>
                <h3 className="font-display font-bold text-white text-xl mb-3">
                  {b.title}
                </h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-24 bg-zinc-950">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 lg:sticky lg:top-32 self-start space-y-5">
            <div className="flex items-center gap-3">
              <span className="h-px w-12 bg-sky-400"></span>
              <span className="eyebrow">Our Process</span>
            </div>
            <h2 className="font-display font-black text-white text-3xl sm:text-5xl leading-tight tracking-tighter">
              From first call to final cleanup.
            </h2>
            <p className="text-zinc-400 text-base leading-relaxed">
              A simple, transparent process built around respecting your time
              and your property.
            </p>
          </div>
          <div className="lg:col-span-7 space-y-6">
            {cfg.process.map((step, i) => (
              <div
                key={i}
                data-testid={`process-step-${i}`}
                className="flex gap-6 border-t border-white/10 pt-6"
              >
                <div className="font-display font-black text-sky-400 text-3xl leading-none w-12 shrink-0">
                  0{i + 1}
                </div>
                <p className="text-zinc-200 text-base sm:text-lg leading-relaxed pt-1">
                  {step}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CITIES SERVED */}
      <section className="py-24 bg-[#09090b]" data-testid="cities-served">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-12">
            <div className="space-y-4 max-w-2xl">
              <div className="flex items-center gap-3">
                <span className="h-px w-12 bg-sky-400"></span>
                <span className="eyebrow">{t.services.location_pages}</span>
              </div>
              <h2 className="font-display font-black text-white text-3xl sm:text-5xl leading-tight tracking-tighter">
                {t.services.quote_coverage}
              </h2>
              <p className="text-zinc-400">
                {t.services.quote_availability}
              </p>
            </div>
            <a href="tel:+15105079318" className="btn-secondary">
              <Phone size={16} />
              (510) 507-9318
            </a>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-white/5">
            {CITIES.map((c, i) => {
              const slug = c.name.toLowerCase().replace(/\s+/g, "-");
              const inner = (
                <>
                  <MapPin
                    size={16}
                    className="text-sky-400 mt-1 shrink-0 group-hover:scale-110 transition-transform"
                  />
                  <div>
                    <div className="font-display font-bold text-white group-hover:text-sky-400 transition-colors">
                      {c.name}
                    </div>
                    <div className="text-zinc-500 text-xs mt-0.5">{c.region}</div>
                  </div>
                </>
              );
              const cls = "bg-zinc-950 hover:bg-sky-500/5 transition-colors p-5 flex items-start gap-3 group";
              const hasCityPage = ["san-jose","sacramento","concord","vacaville","modesto","santa-rosa","bay-area","reno"].includes(slug);
              return hasCityPage ? (
                <Link
                  key={c.name}
                  data-testid={`city-${i}`}
                  to={`/locations/${slug}`}
                  className={cls}
                >
                  {inner}
                </Link>
              ) : (
                <div
                  key={c.name}
                  data-testid={`city-${i}`}
                  className={cls}
                >
                  {inner}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* BRANDS */}
      <section className="py-20 bg-zinc-950 border-y border-white/5">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="eyebrow mb-8 text-center">Brands We Install</div>
          <div className="flex flex-wrap justify-center items-center gap-x-10 gap-y-5">
            {cfg.brands.map((b) => (
              <span
                key={b}
                className="font-display font-bold text-white/70 hover:text-white transition-colors text-lg sm:text-xl tracking-wide"
              >
                {b}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-[#09090b]" data-testid="faq-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4 lg:sticky lg:top-32 self-start space-y-5">
            <div className="flex items-center gap-3">
              <span className="h-px w-12 bg-sky-400"></span>
              <span className="eyebrow">FAQ</span>
            </div>
            <h2 className="font-display font-black text-white text-3xl sm:text-5xl leading-tight tracking-tighter">
              Common questions
            </h2>
            <p className="text-zinc-400">
              Don't see your question? Call{" "}
              <a
                href="tel:+15105079318"
                className="text-sky-400 hover:underline"
              >
                (510) 507-9318
              </a>{" "}
              or request a quote.
            </p>
          </div>
          <div className="lg:col-span-8">
            {cfg.faq.map((f, i) => (
              <FAQItem key={i} index={i} q={f.q} a={f.a} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 sm:py-32 bg-[#09090b] relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(14,165,233,0.15)_0%,_transparent_60%)]"></div>
        <div className="relative mx-auto max-w-4xl px-6 lg:px-8 text-center space-y-8">
          <div className="inline-flex items-center gap-3">
            <span className="h-px w-12 bg-sky-400"></span>
            <span className="eyebrow">{t.cta.eyebrow}</span>
            <span className="h-px w-12 bg-sky-400"></span>
          </div>
          <h2 className="font-display font-black text-white text-4xl sm:text-6xl leading-[0.95] tracking-tighter">
            Ready to start your project?
          </h2>
          <p className="text-zinc-400 text-lg max-w-xl mx-auto">
            {t.cta.subtitle}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <a href="tel:+15105079318" className="btn-primary">
              <Phone size={16} />
              Call Now · (510) 507-9318
            </a>
            <button onClick={openQuote} className="btn-secondary">
              {cfg.cta}
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </section>

      {/* Related services */}
      <section className="py-16 bg-zinc-950 border-t border-white/5">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="eyebrow mb-6">Related Services</div>
          <div className="flex flex-wrap gap-3">
            {Object.values(SERVICE_PAGES)
              .filter((p) => p.slug !== cfg.slug)
              .map((p) => (
                <Link
                  key={p.slug}
                  to={`/${p.slug}`}
                  data-testid={`related-${p.slug}`}
                  className="inline-flex items-center gap-2 border border-white/15 px-5 py-3 text-sm font-display font-semibold text-white hover:border-sky-400 hover:text-sky-400 transition-colors"
                >
                  {p.eyebrow}
                  <ArrowRight size={14} />
                </Link>
              ))}
          </div>
        </div>
      </section>

      <Footer />
      <QuoteFormDialog open={quoteOpen} onOpenChange={setQuoteOpen} />
    </div>
  );
};

const Trust = ({ Icon, label }) => (
  <div className="flex items-center gap-2 text-zinc-300 text-sm">
    <Icon size={14} className="text-sky-400" />
    <span>{label}</span>
  </div>
);

export default ServicePage;
